/// <amd-module name="Test/TestPlansHubRefresh/TestCaseResultsPanel/Component/TestCaseResultsPanelConnectedView" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import * as Reselect from "Reselect";
import { ITestPointItem } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsContracts";
import { TestCaseResultsPanelActions } from "Test/TestPlansHubRefresh/TestCaseResultsPanel/Redux/TestCaseResultsPanelActions";
import { IResultsPanelAwareState, IResultsPanelTableItem } from "Test/TestPlansHubRefresh/TestCaseResultsPanel/Redux/TestCaseResultsPanelContracts";
import * as Resources from "Test/TestPlansHubRefresh/TestCaseResultsPanel/Resources";
import * as TestCaseResultsPanelConstants from "Test/TestPlansHubRefresh/TestCaseResultsPanel/TestCaseResultsPanelConstants";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { ContentSize } from "VSSUI/Callout";
import { CustomCard } from "VSSUI/Card";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Link } from "VSSUI/Link";
import { IListSelection, ListSelection } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelCloseButton, PanelContent, PanelFooter } from "VSSUI/Panel";
import { ConnectedHeaderCommandBar } from "VSSUI/ReduxHeaderCommandBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ITableColumn, renderSimpleCell, Table } from "VSSUI/Table";
import { ZeroData } from "VSSUI/ZeroData";
import { getResultsPanelModule } from "../Redux/TestCaseResultsPanelModule";

export interface ITestCaseResultsPanelProps extends IVssComponentProperties {
    showPanel: ObservableValue<boolean>;
    testPoints: ITestPointItem[];
    selectedIndex: number;
    loadingInProgress: boolean;
    testCaseResults: IResultsPanelTableItem[];
    updateSelection(next: boolean): void;
    fetchTestCaseResults(testCaseId: number): void;
    openRunsPage(pointId: number, runId: number, resultId: number): void;
    openExecutionHistory(pointId: number, testCaseId: number): void;
}

/**
 * TestCaseResultsPanelView: Renders the test case results panel
 */
export class TestCaseResultsPanelView extends VssComponent<ITestCaseResultsPanelProps, IVssComponentContext> {
    private _testCaseName: ObservableValue<string>;
    private _testCaseId: number;
    private _selectedIndex: number;
    private _tableColumns: ITableColumn<any>[];
    private _items: ObservableArray<IResultsPanelTableItem>;
    private _resultsTableSelection: IListSelection;

    constructor(props: ITestCaseResultsPanelProps, context?: IVssComponentContext) {
        super(props);
        this._items = new ObservableArray<IResultsPanelTableItem>();
        this._tableColumns = this._getColumns();
        this._resultsTableSelection = new ListSelection(true);
        this._selectedIndex = props.selectedIndex;
        this._testCaseName = new ObservableValue<string>(this._getSelectedTestCaseName(this._selectedIndex, props.testPoints));
        this._testCaseId = this._getSelectedTestCaseId(this._selectedIndex, props.testPoints);
    }

    public shouldComponentUpdate(nextProps: ITestCaseResultsPanelProps) {
        if (nextProps.testCaseResults) {
            this._items.removeAll();
            for (let i = 0; i < nextProps.testCaseResults.length; i++) {
                this._items.push(nextProps.testCaseResults[i]);
            }
        }

        return true;
    }

    public render(): JSX.Element {
        let card = <Spinner className="spinner-card flex-grow" size={SpinnerSize.medium} />;
        if (!this.props.loadingInProgress) {
            if (this._items && this._items.length > 0) {
                card = (
                    <CustomCard className="testplans-hub-results-panel-card">
                        <Table
                            className="testplans-hub-results-panel-table"
                            containerClassName="custom-scrollbar"
                            columns={this._tableColumns}
                            scrollable={true}
                            itemProvider={this._items}
                            showLines={true}
                            onActivate={this._openRunsPage}
                            singleClickActivation={false}
                            selection={this._resultsTableSelection}
                        />
                    </CustomCard>
                );
            }
            else {
                const image = getAssetLocation(this.context.pageContext, TestCaseResultsPanelConstants.ZeroDataImagePath);

                card = (
                    <>
                        <ZeroData
                            imagePath={image}
                            className="testcase-results-panel-zero-data"
                            imageAltText={Resources.NoResultsFound}
                            primaryText={Resources.NoResultsFound}
                            secondaryText={Resources.NoResultsFoundSecondary}
                        />
                    </>
                );
            }
        }
        return (
            <DynamicModuleLoader modules={[getResultsPanelModule(this._testCaseId)]}>
                <CustomPanel lightDismiss={false} onDismiss={this._hidePanel} size={ContentSize.Large} calloutClassName="testplans-hub-panel-callout">
                    <CustomHeader>
                        <HeaderTitleArea>
                            <div>
                                <Observer testCaseName={this._testCaseName}>
                                    {(props: { testCaseName: string }) => {
                                        return (
                                            <div className="testcase-name-and-spinner">
                                                <div className="bolt-table-header-cell-text text-ellipsis testcase-name">{props.testCaseName}</div>
                                                {this.props.loadingInProgress && <Spinner className="spinner-top" size={SpinnerSize.medium} />}
                                            </div>
                                        )
                                    }}
                                </Observer>
                                <div className="test-case-results-panel-command-bar">
                                    <ConnectedHeaderCommandBar
                                        showFilter={false}
                                        commandsSelector={CommandSelector}
                                        viewActionsSelector={this._viewActions}
                                    />
                                </div>
                            </div>
                            <div className="results-panel-header title-m">{Resources.TestCaseResults}</div>
                        </HeaderTitleArea>
                        <PanelCloseButton onDismiss={this._hidePanel} />
                    </CustomHeader>
                    <PanelContent>
                        <div className="flex-grow">{card}</div>
                    </PanelContent>
                    <PanelFooter>
                        <Link onClick={this._openExecutionHistoryForTestPoint}>
                            <span>{Resources.ExecutionHistoryForTestPoint}</span>
                        </Link>
                    </PanelFooter>
                </CustomPanel>
            </DynamicModuleLoader>
        );
    }

    private _getColumns(): ITableColumn<any>[] {
        const tableColumns: ITableColumn<any>[] = [];
        tableColumns.push(
            {
                id: TestCaseResultsPanelConstants.OutcomeColumn,
                name: Resources.OutcomeColumnHeader,
                width: new ObservableValue(130),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: TestCaseResultsPanelConstants.TimeStampColumn,
                name: Resources.TimeStampColumnHeader,
                width: new ObservableValue(80),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: TestCaseResultsPanelConstants.ConfigurationColumn,
                name: Resources.ConfigurationColumnHeader,
                width: new ObservableValue(100),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: TestCaseResultsPanelConstants.RunByColumn,
                name: Resources.RunByColumnHeader,
                width: new ObservableValue(130),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: TestCaseResultsPanelConstants.TesterColumn,
                name: Resources.TesterColumnHeader,
                width: new ObservableValue(130),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: TestCaseResultsPanelConstants.TestPlanColumn,
                name: Resources.TestPlanNameColumnHeader,
                width: new ObservableValue(100),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            },
            {
                id: TestCaseResultsPanelConstants.TestSuiteColumn,
                name: Resources.TestSuiteNameColumnHeader,
                width: new ObservableValue(100),
                onSize: this._onColumnSize,
                renderCell: renderSimpleCell
            }
        );
        return tableColumns;
    }

    private _onColumnSize = (event: MouseEvent, columnIndex: number, width: number) => {
        const originalColumnWidth = this._tableColumns[columnIndex].width;
        (originalColumnWidth as ObservableValue<number>).value = width;
    };

    private _getSelectedTestCaseName(selectedIndex: number, testPoints: ITestPointItem[]): string {
        const testCaseName: string = testPoints[selectedIndex].testCaseRef.title;
        return testCaseName;
    }

    private _getSelectedTestCaseId(selectedIndex: number, testPoints: ITestPointItem[]): number {
        const testCaseId: number = testPoints[selectedIndex].testCaseRef.id;
        return testCaseId;
    }

    // open the runs page for the selected test run
    private _openRunsPage = (): void => {
        const testCaseIndex = this._resultsTableSelection.value[0].beginIndex;

        const pointId: number = this._items.value[testCaseIndex].pointId;
        const runId: number = this._items.value[testCaseIndex].runId;
        const resultId: number = this._items.value[testCaseIndex].resultId;
        this.props.openRunsPage(pointId, runId, resultId);
    };

    private _openExecutionHistoryForTestPoint = (): void => {
        const testCaseId: number = this._testCaseId;
        const testPointId: number = this.props.testPoints[this._selectedIndex].id;
        this.props.openExecutionHistory(testPointId, testCaseId);
    };

    private _hidePanel = () => {
        this.props.showPanel.value = false;
    };

    private _viewActions = (): IHeaderCommandBarItem[] => {
        const commands: IHeaderCommandBarItem[] = [];
        commands.push(
            {
                id: "previous-test-point",
                iconProps: {
                    iconName: "Up"
                },
                subtle: true,
                onActivate: () => {
                    this._switchTestPoint(false);
                    this.props.updateSelection(false);
                },
                className: "",
                tooltipProps: {
                    text: Resources.PreviousTestPoint
                }
            },
            {
                id: "next-test-point",
                iconProps: {
                    iconName: "Down"
                },
                subtle: true,
                onActivate: () => {
                    this._switchTestPoint(true);
                    this.props.updateSelection(true);
                },
                className: "",
                tooltipProps: {
                    text: Resources.NextTestPoint
                }
            }
        );
        return commands;
    };

    /*
    show test case results corresponding to the next or the previous test point
    ** @next - true when the next test point is to be selected and false when the previous test point is to be selected
    */
    private _switchTestPoint(next: boolean): void {
        if (next) {
            if (this.props.testPoints.length === this._selectedIndex + 1) {
                this._selectedIndex = 0;
            }
            else {
                this._selectedIndex = this._selectedIndex + 1;
            }
        }
        else {
            if (this._selectedIndex === 0) {
                this._selectedIndex = this.props.testPoints.length - 1;
            }
            else {
                this._selectedIndex = this._selectedIndex - 1;
            }
        }
        if (this.props.testPoints[this._selectedIndex].testCaseRef.id != this._testCaseId) {
            let testPointId: number = this.props.testPoints[this._selectedIndex].id;
            this._testCaseId = this.props.testPoints[this._selectedIndex].testCaseRef.id;
            this._testCaseName.value = this.props.testPoints[this._selectedIndex].testCaseRef.title;
            this.props.fetchTestCaseResults(this._testCaseId);
        }
    }
}

export const CommandSelector = Reselect.createSelector(
    // Just a placeholder for now; the ConnectedHeaderCommandBar requires this but we don't have any commands
    [() => { }],
    () => {
        return [];
    }
);

function mapStateToProps(state: IResultsPanelAwareState): Partial<ITestCaseResultsPanelProps> {
    let testCaseResults, loadingInProgress;
    if (state && state.resultsPanelState) {
        testCaseResults = state.resultsPanelState.testCaseResults;
        loadingInProgress = state.resultsPanelState.loadingInProgress;
    }
    return {
        testCaseResults: testCaseResults,
        loadingInProgress: loadingInProgress
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<ITestCaseResultsPanelProps> {
    return {
        fetchTestCaseResults: (testCaseId: number) => dispatch(TestCaseResultsPanelActions.fetchTestCaseResults(testCaseId)),
        openRunsPage: (pointId: number, runId: number, resultId: number) => dispatch(TestCaseResultsPanelActions.openRunsHub(pointId, runId, resultId)),
        openExecutionHistory: (testPointId: number, testCaseId: number) => dispatch(TestCaseResultsPanelActions.openTestExecutionHistory(testPointId, testCaseId))
    }
}

export const TestCaseResultsPanelConnectedView = connect(
    mapStateToProps,
    mapDispatchToProps
)(TestCaseResultsPanelView);

Components.add("testcase-results-panel", TestCaseResultsPanelConnectedView);
